import { CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter, useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useCustomers, useCustomersByName } from '@src/hooks/useCustomer';
import { useCustomerMarketAccounts } from '@src/providers';
import {
  ACTION,
  EntityAdmin,
  IconName,
  MarketAccountTypeEnum,
  useMarketAccountsContext,
  useSymbolsFilter,
  type FilterableProperty,
  type MarketAccount,
} from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import {
  CUSTOMER_ACCOUNT_CONFIGURATION_COLUMNS,
  CUSTOMER_ACCOUNT_CONFIGURATION_KEY_FILTER,
  getCustomerAccountConfigurationDisplayName,
  getCustomerAccountConfigurationDrawerEntity,
  getCustomerAccountConfigurationDrawerOptions,
  getCustomerAccountConfigurationEntityForSave,
  isSupportedCustomerAccountConfigurationKey,
  toCustomerAccountConfigurationRow,
  type CustomerAccountConfigurationDrawerEntity,
  type CustomerAccountConfigurationRow,
} from './utils';

export function CustomerAccountConfigurations() {
  const { isAuthorized } = useRoleAuth();
  const customers = useCustomers();
  const { disableCustomerMarketAccounts } = useFeatureFlag();
  const { marketAccountsList } = useMarketAccountsContext();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  const customersByName = useCustomersByName();
  const configurationMarketAccounts = disableCustomerMarketAccounts
    ? marketAccountsList?.filter(marketAccount => marketAccount.Type === MarketAccountTypeEnum.Customer)
    : customerMarketAccountsList;
  const canManageConfigurations = isAuthorized(ACTION.EDIT_DEALER) || isAuthorized(ACTION.EDIT_DEALER_TRADING);

  const drawerOptions = useMemo(() => {
    if (!customers || !configurationMarketAccounts) {
      return undefined;
    }
    return getCustomerAccountConfigurationDrawerOptions({
      customers,
      customerMarketAccountsList: configurationMarketAccounts,
    });
  }, [configurationMarketAccounts, customers]);

  const counterpartiesFilter = useCounterpartiesFilter({ label: 'Customer' });
  const marketAccountsFilter = useCustomerAccountMarketAccountsFilter(configurationMarketAccounts, customersByName);
  const symbolsFilter = useSymbolsFilter();

  const availableFilterableProperties = useMemo(() => {
    const properties = [
      CUSTOMER_ACCOUNT_CONFIGURATION_KEY_FILTER,
      counterpartiesFilter,
      marketAccountsFilter,
      symbolsFilter,
    ];
    return properties.every(property => !isNil(property)) ? properties : undefined;
  }, [counterpartiesFilter, marketAccountsFilter, symbolsFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  return (
    <EntityAdmin<CustomerAccountConfigurationRow, CustomerAccountConfigurationDrawerEntity>
      title="Customer Account Configurations"
      subtitle="View and manage customer account configuration overrides."
      entityName="Customer Account Configuration"
      path="/customer-account-configurations"
      entityIDField="ConfigID"
      columns={CUSTOMER_ACCOUNT_CONFIGURATION_COLUMNS}
      persistKey="dealer/customer-account-configurations"
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      drawerOptions={drawerOptions}
      allowAddEntity={canManageConfigurations}
      allowEditEntity={canManageConfigurations}
      allowDeleteEntity={canManageConfigurations}
      getEntityDisplayName={getCustomerAccountConfigurationDisplayName}
      getEntityAsDrawerEntity={getCustomerAccountConfigurationDrawerEntity}
      getEntityForPatchPost={getCustomerAccountConfigurationEntityForSave}
      getResponseAsBlotterEntity={toCustomerAccountConfigurationRow}
      serverDataToRecordMappingFunc={toCustomerAccountConfigurationRow}
      userFilterFunc={entity => isSupportedCustomerAccountConfigurationKey(entity.Key)}
    />
  );
}

function useCustomerAccountMarketAccountsFilter(
  configurationMarketAccounts: MarketAccount[] | undefined,
  customersByName: ReturnType<typeof useCustomersByName>
): FilterableProperty<'MarketAccounts'> | undefined {
  return useMemo(() => {
    if (configurationMarketAccounts == null) {
      return undefined;
    }

    const accountsByName = new Map(
      configurationMarketAccounts.map(marketAccount => [marketAccount.Name, marketAccount])
    );

    return {
      key: 'MarketAccounts',
      label: 'Customer Account',
      field: 'MarketAccount',
      icon: IconName.Identification,
      options: configurationMarketAccounts.map(marketAccount => marketAccount.Name),
      getOptionLabel: (marketAccountName: string) => {
        const marketAccount = accountsByName.get(marketAccountName);
        if (marketAccount == null) {
          return marketAccountName;
        }
        const customer = customersByName?.get(marketAccount.Counterparty);
        const accountLabel = marketAccount.SourceAccountID ?? marketAccount.Name;
        if (customer != null) {
          return `${customer.DisplayName ?? customer.Name} - ${accountLabel}`;
        }
        return accountLabel;
      },
      getOptionDescription: (marketAccountName: string) => accountsByName.get(marketAccountName)?.Name ?? '',
      dropdownOptions: {
        dropdownWidth: '350px',
      },
    } as const satisfies FilterableProperty;
  }, [configurationMarketAccounts, customersByName]);
}
